import { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../config";
import "../assets/css/flashcardgame.css";
import "../assets/css/vocabtest.css";
import { fillPlaceholdersForCard, loadLanguagePlaceholderMap, resolveCardDisplayText, scanCardPlaceholders } from "../utils/vocabGameHelpers";
import { speakText, stopSpeech, PRONUNCIATION_PASS_SCORE } from "../utils/audioHelpers";
import { navigateToDashboard, getEffectiveLangId } from "../utils/navigationHelpers";
import { authFetch } from "../utils/auth";
import LoadingScreen from "../components/common/LoadingScreen";
import SpeakerButton from "../components/common/SpeakerButton";
import PronunciationPractice from "../components/common/PronunciationPractice";
import InGameSettingsModal, { SettingsIcon } from "../components/common/InGameSettingsModal";
import BackToDashboardButton from "../components/common/BackToDashboardButton";
import SessionCompleteCard from "../components/common/SessionCompleteCard";
import "../assets/css/pronunciation.css";

const LANG_TAG = { 0: "de-DE", 1: "es-MX", 2: "fr-FR", 3: "ru-RU", 4: "pt-BR" };
const LANG_NAME = { 0: "German", 1: "Mexican Spanish", 2: "French", 3: "Russian", 4: "Brazilian Portuguese" };

function getLangTag(langId) { return LANG_TAG[parseInt(langId, 10)] ?? "de-DE"; }
function getLangName(langId) { return LANG_NAME[parseInt(langId, 10)] ?? "German"; }

function pronunciationWordClassName(word) {
    if (word.error_type === "Omission") {
        return "pronunciation-word pronunciation-word--omission";
    }
    if (word.accuracy_score >= 80) {
        return "pronunciation-word pronunciation-word--good";
    }
    if (word.accuracy_score >= 50) {
        return "pronunciation-word pronunciation-word--ok";
    }
    return "pronunciation-word pronunciation-word--poor";
}

function FeedbackAnswerBlock({ correctText, prompt, langTag, showForeignPrompt = false }) {
    return (
        <>
            <div className="test-feedback-answer-row">
                <span className="test-feedback-answer">Correct answer: {correctText}</span>
                {!showForeignPrompt && (
                    <SpeakerButton text={prompt} lang={langTag} label="Hear pronunciation" />
                )}
            </div>
            {showForeignPrompt && (
                <div className="test-feedback-answer-row test-feedback-answer-row--prompt">
                    <span className="test-feedback-prompt">{prompt}</span>
                    <SpeakerButton text={prompt} lang={langTag} label="Hear pronunciation" />
                </div>
            )}
        </>
    );
}

function removeContext(s) {
    return s.replace(/[([][^)\]]*[)\]]/g, "").trim();
}

function normalizeAnswer(s) {
    return removeContext(s).toLowerCase().replace(/[^a-z0-9\s]/g, "");
}

function formatChipWord(word) {
    return word.trim().toLowerCase();
}

function levenshtein(a, b) {
    const m = a.length, n = b.length;
    let prev = Array.from({ length: n + 1 }, (_, i) => i);
    for (let i = 1; i <= m; i++) {
        const cur = [i];
        for (let j = 1; j <= n; j++) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        }
        prev = cur;
    }
    return prev[n];
}

function shuffleArray(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

function pickDistractorWords(cards, index, correctSet) {
    const distractors = [];
    const candidates = [];
    cards.forEach((c, idx) => {
        if (idx !== index) {
            const { prompt } = resolveCardDisplayText(c);
            candidates.push(...prompt.split(/\s+/).map(formatChipWord));
        }
    });
    const uniqueCandidates = [...new Set(candidates)].filter(Boolean);
    while (distractors.length < 6 && uniqueCandidates.length > 0) {
        const word = uniqueCandidates.splice(Math.floor(Math.random() * uniqueCandidates.length), 1)[0];
        if (!correctSet.includes(word)) {
            distractors.push(word);
        }
    }
    while (distractors.length < 6) {
        const words = ["und", "ist", "ich", "du", "sie", "es", "wir", "ihr", "mein", "dein"];
        const word = words[Math.floor(Math.random() * words.length)];
        if (!correctSet.includes(word) && !distractors.includes(word)) {
            distractors.push(word);
        }
    }
    return distractors;
}

function initRound(cards, index, placeholderMap, allowedTypes) {
    fillPlaceholdersForCard(cards[index], placeholderMap);
    const qType = allowedTypes[Math.floor(Math.random() * allowedTypes.length)] || 1;
    const round = { questionType: qType, typedAnswer: "", feedback: null, answerChips: [], bankChips: [] };
    if (qType === 2) {
        const { prompt } = resolveCardDisplayText(cards[index]);
        const cleanedPrompt = removeContext(prompt);
        const correctWords = cleanedPrompt.split(/\s+/).filter(Boolean).map(formatChipWord);
        const distractors = pickDistractorWords(cards, index, correctWords);
        round.bankChips = shuffleArray([...correctWords, ...distractors].map((word, i) => ({ id: `${index}-${i}-${word}`, word })));
    }
    return round;
}

function ReviewGame() {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const languageId = getEffectiveLangId(urlSearchParams.get("lang_id"));
    const songId = urlSearchParams.get("song_id");
    const langTag = getLangTag(languageId);
    const langName = getLangName(languageId);

    const [cards, setCards] = useState([]);
    const [placeholderMap, setPlaceholderMap] = useState(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [correctCardIds, setCorrectCardIds] = useState([]);
    const [initialScores, setInitialScores] = useState({});

    const [questionType, setQuestionType] = useState(null);
    const [typedAnswer, setTypedAnswer] = useState("");
    const [answerChips, setAnswerChips] = useState([]);
    const [bankChips, setBankChips] = useState([]);
    const [feedback, setFeedback] = useState(null);
    const [pronunciationAttempt, setPronunciationAttempt] = useState(0);
    const [sessionDone, setSessionDone] = useState(false);

    const [showSettings, setShowSettings] = useState(false);
    const [autoPlayAudio, setAutoPlayAudio] = useState(true);
    const [includeListening, setIncludeListening] = useState(true);
    const [includeSpeaking, setIncludeSpeaking] = useState(true);
    const [includeTranslation, setIncludeTranslation] = useState(true);

    const includeListeningRef = useRef(includeListening);
    const includeSpeakingRef = useRef(includeSpeaking);
    const includeTranslationRef = useRef(includeTranslation);

    useEffect(() => { includeListeningRef.current = includeListening; }, [includeListening]);
    useEffect(() => { includeSpeakingRef.current = includeSpeaking; }, [includeSpeaking]);
    useEffect(() => { includeTranslationRef.current = includeTranslation; }, [includeTranslation]);

    const numPlaceholders = 5;
    const inputRef = useRef(null);
    const correctCountRef = useRef(0);

    const handleExit = () => {
        if (songId) {
            window.location.href = `/song?song_id=${songId}`;
        } else {
            navigateToDashboard("review");
        }
    };

    useEffect(() => {
        if (languageId) {
            const url = songId
                ? `${API_BASE_URL}/api/review-deck?lang_id=${languageId}&song_id=${songId}&limit=30`
                : `${API_BASE_URL}/api/review-deck?lang_id=${languageId}&limit=30`;
            authFetch(url)
                .then(res => res.json())
                .then(data => {
                    if (data.cards) {
                        const initialCards = data.cards.map(card => ({
                            ...card,
                            placeholders: []
                        }));

                        // Save initial scores map to calculate improvements at the end
                        const scores = {};
                        initialCards.forEach(c => {
                            scores[c.id] = c.familiarity_score ?? 0;
                        });
                        setInitialScores(scores);

                        const placeholders = scanCardPlaceholders(initialCards);

                        loadLanguagePlaceholderMap(languageId, placeholders, numPlaceholders)
                            .then(randomCodes => {
                                setPlaceholderMap(randomCodes);
                                if (initialCards.length > 0) {
                                    fillPlaceholdersForCard(initialCards[0], randomCodes);
                                }
                            })
                            .catch(err => {
                                console.error("Failed to load placeholders for language:", languageId, err);
                                setPlaceholderMap({});
                            });

                        setCards(initialCards);
                    } else {
                        setError(true);
                    }
                })
                .catch(e => {
                    setError(true);
                    console.error(e);
                })
                .finally(() => {
                    setLoading(false);
                });
        } else {
            setError(true);
            setLoading(false);
        }
        return () => stopSpeech();
    }, [languageId, songId]);

    useEffect(() => {
        if (cards.length === 0 || placeholderMap === null || sessionDone || currentIndex >= cards.length) return;

        const allowedTypes = [];
        if (includeTranslationRef.current) allowedTypes.push(1, 2);
        if (includeListeningRef.current) allowedTypes.push(3);
        if (includeSpeakingRef.current) allowedTypes.push(4);
        if (allowedTypes.length === 0) allowedTypes.push(1);

        const round = initRound(cards, currentIndex, placeholderMap, allowedTypes);
        setQuestionType(round.questionType);
        setTypedAnswer(round.typedAnswer);
        setFeedback(round.feedback);
        setAnswerChips(round.answerChips);
        setBankChips(round.bankChips);
        setPronunciationAttempt(0);
    }, [currentIndex, cards, placeholderMap, sessionDone]);

    useEffect(() => {
        if ((questionType === 1 || questionType === 3) && !feedback && inputRef.current) {
            inputRef.current.focus();
        }
    }, [questionType, feedback]);

    // Audio Autoplay: TTS spoken text when card changes
    useEffect(() => {
        if (cards.length === 0 || sessionDone || currentIndex >= cards.length) return;
        if (!autoPlayAudio || feedback) return;

        // Pronunciation question (Type 4) doesn't autoplay reference audio before you try speaking
        if (questionType === 4) return;

        const { prompt } = resolveCardDisplayText(cards[currentIndex]);
        // Delay slightly to let screen render
        const timer = setTimeout(() => {
            speakText(prompt, langTag);
        }, 300);
        return () => clearTimeout(timer);
    }, [currentIndex, questionType, feedback, sessionDone, cards, autoPlayAudio, langTag]);

    function parseAlternativeAnswers(translation) {
        return translation
            .split('/')
            .map(alt => normalizeAnswer(alt.trim()))
            .filter(alt => alt.length > 0);
    }

    function userAnswerMatchesAny(userAnswer, translation) {
        const alternatives = parseAlternativeAnswers(translation);
        const normalized = normalizeAnswer(userAnswer);
        return alternatives.some(
            alt => normalized === alt || levenshtein(normalized, alt) <= 1
        );
    }

    function checkType1Answer(card) {
        const { translation } = resolveCardDisplayText(card);
        return userAnswerMatchesAny(typedAnswer, translation);
    }

    function checkType2Answer(card) {
        const { prompt } = resolveCardDisplayText(card);
        const expected = normalizeAnswer(prompt);
        const given = normalizeAnswer(answerChips.map(c => c.word).join(" "));
        return given === expected;
    }

    function handlePronunciationRedo() {
        if (feedback && feedback.isCorrect) {
            correctCountRef.current = Math.max(0, correctCountRef.current - 1);
            setCorrectCount(correctCountRef.current);
            setCorrectCardIds(prev => prev.filter(id => id !== cards[currentIndex].id));
        }
        setFeedback(null);
        setPronunciationAttempt((prev) => prev + 1);
    }

    function handlePronunciationResult(result) {
        if (feedback) return;
        const { prompt } = resolveCardDisplayText(cards[currentIndex]);
        const isCorrect = result.overall_score >= PRONUNCIATION_PASS_SCORE;
        setFeedback({ isCorrect, correctText: prompt, pronunciationResult: result });
        if (isCorrect) {
            correctCountRef.current += 1;
            setCorrectCount(correctCountRef.current);
            setCorrectCardIds(prev => [...prev, cards[currentIndex].id]);
        }

        // Fire-and-forget prompt familiarity upsert
        if (cards[currentIndex].id != null) {
            authFetch(`${API_BASE_URL}/api/upsert-prompt-familiarity`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    prompt_id: cards[currentIndex].id,
                    familiarity_score: isCorrect ? 1.0 : 0.0
                })
            }).catch(e => console.warn("Failed to save prompt familiarity:", e));
        }
    }

    function handleSubmit(e) {
        if (e) e.preventDefault();
        if (feedback) return;
        const card = cards[currentIndex];
        const { prompt, translation } = resolveCardDisplayText(card);

        const isCorrect = questionType === 2 ? checkType2Answer(card) : checkType1Answer(card);
        const correctText = removeContext(questionType === 2 ? prompt : translation);
        const foreignPrompt = questionType === 3 ? prompt : undefined;
        setFeedback({ isCorrect, correctText, foreignPrompt });
        if (isCorrect) {
            correctCountRef.current += 1;
            setCorrectCount(correctCountRef.current);
            setCorrectCardIds(prev => [...prev, card.id]);
        }

        // Fire-and-forget prompt familiarity upsert
        if (card.id != null) {
            authFetch(`${API_BASE_URL}/api/upsert-prompt-familiarity`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    prompt_id: card.id,
                    familiarity_score: isCorrect ? 1.0 : 0.0
                })
            }).catch(e => console.warn("Failed to save prompt familiarity:", e));
        }
    }

    function handleNext() {
        stopSpeech();
        if (currentIndex + 1 >= cards.length) {
            setSessionDone(true);
        } else {
            setCurrentIndex(prev => prev + 1);
        }
    }

    function moveChipToAnswer(chip) {
        setBankChips(prev => prev.filter(c => c.id !== chip.id));
        setAnswerChips(prev => [...prev, chip]);
    }

    function moveChipToBank(chip) {
        setAnswerChips(prev => prev.filter(c => c.id !== chip.id));
        setBankChips(prev => [...prev, chip]);
    }

    const settingsMenu = (
        <InGameSettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            title="Settings"
        >
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={autoPlayAudio}
                        onChange={e => setAutoPlayAudio(e.target.checked)}
                        className="in-game-settings-checkbox"
                    />
                    Autoplay audio pronunciation
                </label>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={includeTranslation}
                        onChange={e => {
                            if (!e.target.checked && !includeListening && !includeSpeaking) return;
                            setIncludeTranslation(e.target.checked);
                        }}
                        className="in-game-settings-checkbox"
                    />
                    Include translation questions
                </label>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={includeListening}
                        onChange={e => {
                            if (!e.target.checked && !includeTranslation && !includeSpeaking) return;
                            setIncludeListening(e.target.checked);
                        }}
                        className="in-game-settings-checkbox"
                    />
                    Include listening questions
                </label>
            </div>
            <div className="in-game-settings-section">
                <label className="in-game-settings-checkbox-label">
                    <input
                        type="checkbox"
                        checked={includeSpeaking}
                        onChange={e => {
                            if (!e.target.checked && !includeTranslation && !includeListening) return;
                            setIncludeSpeaking(e.target.checked);
                        }}
                        className="in-game-settings-checkbox"
                    />
                    Include speaking questions
                </label>
            </div>
        </InGameSettingsModal>
    );

    if (loading) {
        return (
            <LoadingScreen
                title={songId ? "Loading song review..." : "Loading review session..."}
                onBack={handleExit}
                backLabel="Dashboard"
                onSettingsClick={() => setShowSettings(true)}
                settingsMenu={settingsMenu}
            />
        );
    }

    if (error) {
        return (
            <div className="vocab-test-loading">
                <h1>{songId ? "Error Loading Song Review" : "Error Loading Review"}</h1>
                <p>{songId ? "Could not load song review deck." : "Could not build review deck."}</p>
                <BackToDashboardButton centered onClick={handleExit} />
            </div>
        );
    }

    if (cards.length === 0) {
        return (
            <div className="vocab-test-loading">
                <h1>{songId ? "No song words to review!" : "Nothing to review!"}</h1>
                <p>{songId ? "No saved words for this song yet." : "No weak cards in this language yet."}</p>
                <BackToDashboardButton centered onClick={handleExit} />
            </div>
        );
    }

    if (sessionDone) {
        const totalCount = cards.length;
        const percent = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
        const improvedCards = correctCardIds.filter(id => initialScores[id] < 0.6);

        const stats = [
            { label: "Correct answers", value: `${correctCount} / ${totalCount} (${percent}%)` }
        ];
        if (improvedCards.length > 0) {
            stats.push({
                label: "Improved cards",
                value: `${improvedCards.length} weak card${improvedCards.length > 1 ? "s" : ""}`,
                highlight: true
            });
        }

        return (
            <SessionCompleteCard
                title="Review Complete"
                stats={stats}
                onBack={handleExit}
            />
        );
    }

    const card = cards[currentIndex];
    const { prompt, translation } = resolveCardDisplayText(card);
    const totalCount = cards.length;

    return (
        <div className="vocab-test">
            <BackToDashboardButton floating onClick={handleExit} />
            <button
                type="button"
                aria-label="Settings"
                className="in-game-settings-btn"
                onClick={() => setShowSettings(true)}
            >
                <SettingsIcon size={28} />
            </button>
            {settingsMenu}
            <h1>{songId ? "Song Word Review" : "Weak Word Review"}</h1>
            <div className="vocab-test-progress">Card {currentIndex + 1} of {totalCount}</div>
            <div className="vocab-test-box">
                {questionType === 1 && (
                    <>
                        <div className="test-prompt-row">
                            <div className="test-prompt">{prompt}</div>
                            <SpeakerButton text={prompt} lang={langTag} label="Hear pronunciation" className="speaker-btn--card-front" />
                        </div>
                        <form onSubmit={handleSubmit}>
                            <input ref={inputRef} type="text" className="test-input" value={typedAnswer} onChange={e => setTypedAnswer(e.target.value)} disabled={!!feedback} autoComplete="off" />
                            {!feedback && <button type="submit" className="test-submit-btn">Submit</button>}
                        </form>
                    </>
                )}
                {questionType === 2 && (
                    <>
                        <div className="test-prompt">{translation}</div>
                        <div className="test-answer-area">
                            {answerChips.map(chip => (
                                <button key={chip.id} type="button" className="test-word-chip test-word-chip--selected" onClick={() => !feedback && moveChipToBank(chip)} disabled={!!feedback}>{chip.word}</button>
                            ))}
                        </div>
                        <div className="test-word-bank">
                            {bankChips.map(chip => (
                                <button key={chip.id} type="button" className="test-word-chip" onClick={() => !feedback && moveChipToAnswer(chip)} disabled={!!feedback}>{chip.word}</button>
                            ))}
                        </div>
                        {!feedback && <button type="button" className="test-submit-btn" onClick={handleSubmit} disabled={answerChips.length === 0}>Submit</button>}
                    </>
                )}
                {questionType === 3 && (
                    <>
                        <div className="test-listen-area">
                            <SpeakerButton text={prompt} lang={langTag} size={40} label="Play word" className="speaker-btn--listen" />
                            <div className="test-listen-hint">Tap to hear again</div>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <input ref={inputRef} type="text" className="test-input" value={typedAnswer} onChange={e => setTypedAnswer(e.target.value)} disabled={!!feedback} autoComplete="off" />
                            {!feedback && <button type="submit" className="test-submit-btn">Submit</button>}
                        </form>
                    </>
                )}
                {questionType === 4 && (
                    <>
                        <div className="test-prompt">{translation}</div>
                        <div className="test-listen-hint">Say this in {langName}:</div>
                        {!feedback && (
                            <div className="test-pronunciation-wrap">
                                <PronunciationPractice
                                    key={`${currentIndex}-${pronunciationAttempt}`}
                                    text={prompt}
                                    onResult={handlePronunciationResult}
                                    langCode={langTag}
                                />
                            </div>
                        )}
                        {feedback && (
                            <>
                                <div className={`test-feedback ${feedback.isCorrect ? "test-feedback--correct" : "test-feedback--wrong"}`}>
                                    {feedback.isCorrect ? "Correct" : "Incorrect"}
                                </div>
                                <FeedbackAnswerBlock correctText={feedback.correctText} prompt={prompt} langTag={langTag} />
                                {feedback.pronunciationResult && (
                                    <div className="test-pronunciation-feedback">
                                        <div className="pronunciation-overall-label">Overall pronunciation score</div>
                                        <div className="pronunciation-overall-score">
                                            {Math.round(feedback.pronunciationResult.overall_score)}
                                        </div>
                                        <div className="test-pronunciation-word-list">
                                            {feedback.pronunciationResult.words.map((word, index) => (
                                                <div key={`${word.word}-${index}`} className="test-pronunciation-word-item">
                                                    <span
                                                        className={pronunciationWordClassName(word)}
                                                        onClick={() => speakText(word.word, langTag)}
                                                        title="Click to hear pronunciation"
                                                    >
                                                        {word.word}
                                                    </span>
                                                    <span className="test-pronunciation-word-score">
                                                        {word.error_type === "Omission"
                                                            ? "Missed"
                                                            : `${Math.round(word.accuracy_score)}%`}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                <div className="test-feedback-actions">
                                    <button type="button" className="test-redo-btn" onClick={handlePronunciationRedo}>
                                        Try again
                                    </button>
                                    <button type="button" className="test-submit-btn" onClick={handleNext}>Next</button>
                                </div>
                            </>
                        )}
                    </>
                )}
                {feedback && questionType !== 4 && (
                    <div>
                        <div className={`test-feedback ${feedback.isCorrect ? "test-feedback--correct" : "test-feedback--wrong"}`}>
                            {feedback.isCorrect ? "Correct" : "Incorrect"}
                        </div>
                        <FeedbackAnswerBlock
                            correctText={feedback.correctText}
                            prompt={prompt}
                            langTag={langTag}
                            showForeignPrompt={questionType === 3}
                        />
                        <button type="button" className="test-submit-btn" onClick={handleNext}>Next</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ReviewGame;
